import { keysetPageInputSchema, keysetPageSchema, sortSchema } from "@amtana/module";
import { z } from "zod";

import { languageSchema, userSchema } from "./account.schemas.ts";

// The people directory the back office pages through (ADR-033): account owns the rows, so it
// answers the page itself, keyset, never an offset, never the whole table handed to a peer.
// Each person carries when the account was opened and when they were last seen, the last
// authenticated request or session refresh auth reported, written at most once every few
// minutes (`LAST_SEEN_EVERY_MS`), never on every request.

/** Auth reports a person as seen at most once per session in each window this long; account
 * writes it at most once per half of it, however many sessions report. */
export const LAST_SEEN_EVERY_MS = 5 * 60 * 1000;

/** What the directory can order by: the email, the full name, when opened, when last seen. */
export const USER_SORT_FIELDS = ["email", "name", "createdAt", "lastSeenAt"] as const;
export type UserSortField = (typeof USER_SORT_FIELDS)[number];
export const userSortSchema = sortSchema(USER_SORT_FIELDS);
export type UserSort = z.infer<typeof userSortSchema>;

/** A page of people, narrowed by a name or email, when the account was opened (`createdFrom`
 * inclusive, `createdTo` exclusive) and whether they were seen since `seenSince`. */
export const pageUsersInputSchema = keysetPageInputSchema.extend({
  query: z.string().trim().max(200).optional(),
  createdFrom: z.iso.datetime().optional(),
  createdTo: z.iso.datetime().optional(),
  seenSince: z.iso.datetime().optional(),
  sort: userSortSchema.optional(),
});
export type PageUsersInput = z.infer<typeof pageUsersInputSchema>;

/** A person as the directory lists them. `createdAt` is null for an account opened before it was
 * recorded; `lastSeenAt` is null until auth first reports them. */
export const listedUserSchema = userSchema.extend({
  createdAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
  /** The person's own saved language: the funnel's fallback for a sign-up whose first touch kept
   * no landing page (ADR-028 revision 1 point 3, S33.13). */
  language: languageSchema,
});
export type ListedUser = z.infer<typeof listedUserSchema>;

export const userPageSchema = keysetPageSchema(listedUserSchema);
export type UserPage = z.infer<typeof userPageSchema>;

/** When one person's account was opened and when they were last seen. */
export const userActivitySchema = z.object({
  createdAt: z.string().nullable(),
  lastSeenAt: z.string().nullable(),
});
export type UserActivity = z.infer<typeof userActivitySchema>;
